import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// The note's evidence picker. Each tool on the attachment bars clicks its own
// hidden input: the image tool signs photographs, the file tool signs whatever
// the building needs (docs/fitxers.md, 2026-08-14). What each input carries —
// its purpose, its ceiling, its refusal copy — travels in its own dataset, so
// the tool is what declares the gesture. Files go to storage as they are
// picked; the form then posts only signed ids, so title and body stay unsent.
//
// Every file carries its own row: a batch of eight where one is too big has to
// say which one, keep the other seven, and leave the refusal on screen after
// the mosaic comes back (docs/pla-pujada-imatges.md). A refusal that only makes
// a file disappear is the same as losing it.
export default class extends Controller {
  static targets = ["form", "input", "tool", "fields", "list", "template"]
  static values = {
    limit: Number,
    limitMessage: String,
    forbiddenExtensions: Array,
    forbiddenTypeLabel: String,
    failedLabel: String,
    notSavedLabel: String
  }

  connect() {
    this.uploads = new Set()
    this.sent = []
    // The snapshot is taken while this controller is still connected: rows
    // restored from cache would claim files whose uploads died with the page.
    this.onBeforeCache = () => this.reset()
    document.addEventListener("turbo:before-cache", this.onBeforeCache)
    this.syncCapacity()
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.onBeforeCache)
    this.abortAll()
  }

  // Leaving the screen ends the uploads with it. Aborting comes first: a Turbo
  // navigation caches the page while this controller is still connected, and
  // an emptied set is a set that can no longer abort anything — 200 MB would
  // go on travelling to storage for a note nobody is writing any more.
  reset() {
    this.abortAll()
    this.uploads.clear()
    this.sent = []
    this.listTarget.replaceChildren()
    this.fieldsTarget.replaceChildren()
    this.render()
  }

  abortAll() {
    this.uploads.forEach((entry) => entry.xhr?.abort())
  }

  pick(event) {
    if (this.remainingCapacity === 0) return

    this.inputFor(event.params.kind)?.click()
  }

  upload(event) {
    const input = event.target
    if (input.files.length === 0) return

    const remaining = this.remainingCapacity
    if (remaining === 0) {
      input.value = ""
      this.syncCapacity()
      return
    }

    let files = Array.from(input.files)
    if (files.length > remaining) {
      files = files.slice(0, remaining)
      window.alert(this.limitMessageValue)
    }

    // Emptying it lets the same file be picked again: from here on the rows
    // hold the state, not the input.
    input.value = ""
    files.forEach((file) => this.add(file, input))
    this.render()
  }

  add(file, input) {
    const row = this.buildRow(file)
    const entry = { file, input, row, state: "uploading", progress: 0 }
    this.uploads.add(entry)
    this.listTarget.append(row)

    // Refused here so nobody waits for the upload of a file the model is going
    // to refuse anyway. The rules live in NoteAttachment: this is a courtesy,
    // never the gate.
    const maxBytes = Number(input.dataset.maxBytes || 0)
    if (maxBytes > 0 && file.size > maxBytes) {
      entry.state = "rejected"
      entry.message = input.dataset.tooLargeLabel
      return
    }

    if (this.forbidden(file)) {
      entry.state = "rejected"
      entry.message = this.forbiddenTypeLabelValue
      return
    }

    this.start(entry)
  }

  forbidden(file) {
    const extension = file.name.split(".").pop().toLowerCase()
    return this.forbiddenExtensionsValue.includes(extension)
  }

  start(entry) {
    entry.state = "uploading"
    entry.progress = 0

    const upload = new DirectUpload(entry.file, entry.input.dataset.directUploadUrl, {
      directUploadWillStoreFileWithXHR: (xhr) => {
        entry.xhr = xhr
        xhr.upload.addEventListener("progress", (event) => {
          if (!event.lengthComputable) return

          entry.progress = event.loaded / event.total
          this.render()
        })
      }
    })

    upload.create((error, blob) => {
      // Taken off the list while it was in flight: an abort is not a failure
      if (!this.uploads.has(entry)) return

      if (error) {
        entry.state = "failed"
      } else {
        entry.state = "done"
        entry.progress = 1
        entry.field = this.appendField(blob.signed_id)
      }
      this.render()
      this.submitIfReady()
    })
  }

  retry(event) {
    const entry = this.entryFor(event.target)
    if (!entry) return

    // A refusal is not a failure: trying again would refuse it again. And what
    // is already in storage is not uploaded twice — what failed was saving it,
    // so trying again is posting the ids again, not paying for the bytes.
    if (entry.state === "failed") this.start(entry)
    if (entry.state === "unsent") this.submitIfReady()
    this.render()
  }

  remove(event) {
    const entry = this.entryFor(event.target)
    if (!entry) return

    this.uploads.delete(entry)
    this.sent = this.sent.filter((sent) => sent !== entry)
    entry.xhr?.abort()
    entry.field?.remove()
    entry.row.remove()
    this.render()
  }

  // One post, when nothing is still on its way: what landed goes in, and what
  // did not stays on screen waiting for a decision. The batch is named as it
  // leaves, because from here on this controller may only speak about the
  // files that post actually carried — a file that lands while it is in
  // flight belongs to the next one, not to this one.
  submitIfReady() {
    if (this.pending) return
    if (this.sent.length > 0) return

    this.sent = Array.from(this.uploads).filter((entry) => this.sendable(entry))
    if (this.sent.length === 0) return

    this.sent.forEach((entry) => { entry.state = "sent" })
    this.formTarget.requestSubmit()
  }

  // The stream has replaced the mosaic, so the files this post carried are in
  // it now: their rows and their ids go, and only the ones needing a decision
  // remain. Nothing else is touched — clearing the whole form here is how a
  // file picked mid-flight used to vanish without a word.
  //
  // A 422 is an answer: the server kept what was valid and said why it refused
  // the rest, so that batch is spent. Nothing at all, or a 500, means the note
  // never saw those ids — the bytes are still in storage and the batch waits
  // for another attempt instead of disappearing.
  finished(event) {
    const batch = this.sent
    this.sent = []
    const status = event.detail?.fetchResponse?.statusCode

    if (status !== undefined && status < 500) {
      batch.forEach((entry) => {
        this.uploads.delete(entry)
        entry.field?.remove()
        entry.row.remove()
      })
    } else {
      batch.forEach((entry) => { entry.state = "unsent" })
    }

    this.render()
    this.syncCapacity()
    // Whatever landed while that post was in flight is a batch of its own
    if (status !== undefined && status < 500) this.submitIfReady()
  }

  sendable(entry) {
    return entry.state === "done" || entry.state === "unsent"
  }

  render() {
    this.uploads.forEach((entry) => {
      const row = entry.row
      row.querySelector("[data-upload-bar]").style.width = `${Math.round(entry.progress * 100)}%`
      row.querySelector("[data-upload-track]").hidden = entry.state !== "uploading"
      row.querySelector("[data-upload-retry]").hidden =
        entry.state !== "failed" && entry.state !== "unsent"
      row.querySelector("[data-upload-status]").textContent = this.statusFor(entry)
    })

    // An empty list is a border across the screen saying nothing
    this.listTarget.hidden = this.uploads.size === 0
    this.toolTargets.forEach((tool) => {
      if (this.pending) tool.setAttribute("aria-busy", "true")
      else tool.removeAttribute("aria-busy")
    })
  }

  statusFor(entry) {
    if (entry.state === "rejected") return entry.message
    if (entry.state === "failed") return this.failedLabelValue
    // Uploaded and not saved is not the same refusal as not uploaded, and the
    // difference is what tells someone whether their file is anywhere at all
    if (entry.state === "unsent") return this.notSavedLabelValue
    if (entry.state === "done" || entry.state === "sent") return ""

    return `${Math.round(entry.progress * 100)}%`
  }

  buildRow(file) {
    const row = this.templateTarget.content.firstElementChild.cloneNode(true)
    row.querySelector("[data-upload-name]").textContent = file.name
    return row
  }

  appendField(signedId) {
    const field = document.createElement("input")
    field.type = "hidden"
    field.name = "note_attachment[media][]"
    field.value = signedId
    this.fieldsTarget.append(field)
    return field
  }

  entryFor(element) {
    const row = element.closest("[data-upload-row]")
    return Array.from(this.uploads).find((entry) => entry.row === row)
  }

  inputFor(kind) {
    return this.inputTargets.find((input) => input.dataset.uploadKind === kind)
  }

  get pending() {
    return Array.from(this.uploads).some((entry) => entry.state === "uploading")
  }

  get remainingCapacity() {
    const collection = this.element.querySelector("[data-note-attachment-count]")
    const count = Number(collection?.dataset.noteAttachmentCount || 0)
    return Math.max(this.limitValue - count, 0)
  }

  syncCapacity() {
    const full = this.remainingCapacity === 0
    this.toolTargets.forEach((tool) => {
      tool.toggleAttribute("disabled", full)
      tool.setAttribute("aria-disabled", String(full))
    })
  }
}
